import { saveClinicalResource, readClinicalResourceList } from './clinicalMutationAdapter';
import { requireClinicalSaveResult } from '../clinicalSaveResult';
import { isActiveMedication } from '../medicationDisplayUtils';
import { getAuthHeaders, handleResponse } from './common';
import config from '../../config';
import { getSharedChartPermissionMessage } from '../sharedChartAccess';
import { chunkPendingIntakeBatch, PENDING_INTAKE_BATCH_LIMIT } from '../pendingIntakeMedicationBatch';
import {
  isPendingIntakeUnavailableError,
  isPendingIntakeUnavailableHttpResponse,
} from '../pendingIntakeAvailability';

/**
 * Medication API utilities for managing client medications
 * Provides CRUD operations for medications and medication snapshots
 */

export default {
  /**
   * Get current medications for a client
   * @param {string} clientReferenceId - Client reference ID
   * @returns {Promise<Array>} Array of current medications
   */
  getCurrentMedications: async (clientReferenceId, { preferServer = false } = {}) => {
    if (!preferServer) {
      const local = await readClinicalResourceList({ resourceType: 'clientMedication', clientReferenceId, parentReferenceId: clientReferenceId });
      if (local) return local.filter(isActiveMedication);
    }
    try {
      const response = await fetch(`${config.api.url}/clients/${clientReferenceId}/medications`, {
        method: 'GET',
        headers: getAuthHeaders(),
        cache: preferServer ? 'no-store' : 'default',
      });
      
      const medications = await handleResponse(response);
      return medications || [];
    } catch (error) {
      if (getSharedChartPermissionMessage(error)) {
        return [];
      }
      console.error('medicationApi: Error fetching current medications:', error);
      throw error;
    }
  },

  /** Get the complete medication list, including discontinued and completed entries. */
  getMedicationHistory: async (clientReferenceId, { preferServer = false } = {}) => {
    if (!preferServer) {
      const local = await readClinicalResourceList({ resourceType: 'clientMedication', clientReferenceId, parentReferenceId: clientReferenceId });
      if (local) return local;
    }
    const response = await fetch(`${config.api.url}/clients/${clientReferenceId}/medications?includeInactive=true`, {
      method: 'GET',
      headers: getAuthHeaders(),
      cache: 'no-store',
    });
    return (await handleResponse(response)) || [];
  },

  /**
   * Get medication snapshot for a specific context
   * @param {string} clientReferenceId - Client reference ID
   * @param {string} contextType - Type of context (e.g., 'medicalHistory', 'visit')
   * @param {string} contextId - ID of the specific context
   * @returns {Promise<Array>} Array of medications from snapshot
   */
  getMedicationSnapshot: async (clientReferenceId, contextType, contextId) => {
    try {
      const response = await fetch(`${config.api.url}/clients/${clientReferenceId}/medications/snapshots/${contextType}/${contextId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      const medications = await handleResponse(response);
      return medications || [];
    } catch (error) {
      if (getSharedChartPermissionMessage(error)) {
        return [];
      }
      console.error('medicationApi: Error fetching medication snapshot:', error);
      throw error;
    }
  },

  /**
   * Create a new medication for a client
   * @param {string} clientReferenceId - Client reference ID
   * @param {Object} medicationData - Medication data
   * @returns {Promise<Object>} Created medication
   */
  createMedication: async (clientReferenceId, medicationData) => {
    const local = await saveClinicalResource({ resourceType: 'clientMedication', clientReferenceId, parentReferenceId: clientReferenceId, operation: 'create', payload: medicationData });
    if (local) return local;
    try {
      const response = await fetch(`${config.api.url}/clients/${clientReferenceId}/medications`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ dto: medicationData })
      });
      
      const result = await handleResponse(response);
      return requireClinicalSaveResult(result);
    } catch (error) {
      console.error('medicationApi: Error creating medication:', error);
      throw error;
    }
  },

  /**
   * Update an existing medication
   * @param {string} clientReferenceId - Client reference ID
   * @param {number} medicationId - Medication ID
   * @param {Object} medicationData - Updated medication data
   * @returns {Promise<Object>} Updated medication
   */
  updateMedication: async (clientReferenceId, medicationId, medicationData) => {
    const local = await saveClinicalResource({ resourceType: 'clientMedication', clientReferenceId, parentReferenceId: clientReferenceId, resourceReferenceId: medicationId, operation: 'update', payload: medicationData });
    if (local) return local;
    try {
      const response = await fetch(`${config.api.url}/clients/${clientReferenceId}/medications/${medicationId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ dto: medicationData })
      });
      
      const result = await handleResponse(response);
      return requireClinicalSaveResult(result);
    } catch (error) {
      console.error('medicationApi: Error updating medication:', error);
      throw error;
    }
  },

  /**
   * Discontinue a medication
   * @param {string} clientReferenceId - Client reference ID
   * @param {number} medicationId - Medication ID
   * @param {string} reason - Reason for discontinuation
   * @param {string} notes - Additional discontinuation notes
   * @returns {Promise<Object>} Discontinued medication
   */
  discontinueMedication: async (clientReferenceId, medicationId, reason, notes = '') => {
    try {
      const response = await fetch(`${config.api.url}/clients/${clientReferenceId}/medications/${medicationId}/discontinue`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          reason: reason,
          notes: notes.trim() || null,
          discontinueDate: new Date().toISOString() 
        })
      });
      
      const result = await handleResponse(response);
      return requireClinicalSaveResult(result);
    } catch (error) {
      console.error('medicationApi: Error discontinuing medication:', error);
      throw error;
    }
  },

  /**
   * Delete a medication (hard delete)
   * @param {string} clientReferenceId - Client reference ID
   * @param {number} medicationId - Medication ID
   * @returns {Promise<void>}
   */
  deleteMedication: async (clientReferenceId, medicationId) => {
    const local = await saveClinicalResource({ resourceType: 'clientMedication', clientReferenceId, parentReferenceId: clientReferenceId, resourceReferenceId: medicationId, operation: 'delete' });
    if (local) return local;
    try {
      const response = await fetch(`${config.api.url}/clients/${clientReferenceId}/medications/${medicationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      await handleResponse(response);
    } catch (error) {
      console.error('medicationApi: Error deleting medication:', error);
      throw error;
    }
  },

  /**
   * Create a medication snapshot for a specific context
   * @param {string} clientReferenceId - Client reference ID
   * @param {string} contextType - Type of context (e.g., 'medicalHistory', 'visit')
   * @param {string} contextId - ID of the specific context
   * @returns {Promise<Object>} Created snapshot
   */
  createMedicationSnapshot: async (clientReferenceId, contextType, contextId) => {
    try {
      const response = await fetch(`${config.api.url}/clients/${clientReferenceId}/medications/snapshots`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          dto: { 
            contextType, 
            contextId 
          } 
        })
      });
      
      const result = await handleResponse(response);
      return requireClinicalSaveResult(result);
    } catch (error) {
      console.error('medicationApi: Error creating medication snapshot:', error);
      throw error;
    }
  },

  /**
   * Search medications from master database
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of medication suggestions
   */
  searchMedications: async (query) => {
    try {
      const response = await fetch(`${config.api.url}/medications/search?query=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      const results = await handleResponse(response);
      return results || [];
    } catch (error) {
      console.error('medicationApi: Error searching medications:', error);
      throw error;
    }
  },

  /**
   * Pending intake medications awaiting midwife review (vela-api pending-intake routes).
   * @returns {Promise<{ showSection: boolean, items: Array }>}
   */
  getPendingIntakeMedications: async (clientReferenceId) => {
    try {
      const response = await fetch(
        `${config.api.url}/clients/${clientReferenceId}/medications/pending-intake`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      if (response.status === 404) {
        return { showSection: false, items: [] };
      }

      if (await isPendingIntakeUnavailableHttpResponse(response)) {
        return { showSection: false, items: [] };
      }

      const data = await handleResponse(response);
      return {
        showSection: true,
        items: Array.isArray(data?.items) ? data.items : [],
      };
    } catch (error) {
      if (getSharedChartPermissionMessage(error) || isPendingIntakeUnavailableError(error)) {
        return { showSection: false, items: [] };
      }
      throw error;
    }
  },

  acceptPendingIntakeMedication: async (clientReferenceId, intakeEntryKey, overrides = {}) => {
    const encodedKey = encodeURIComponent(intakeEntryKey);
    const body = {};
    if (overrides.name != null && String(overrides.name).trim()) {
      body.name = String(overrides.name).trim();
    }
    if (overrides.dose != null && String(overrides.dose).trim()) {
      body.dose = String(overrides.dose).trim();
    }
    if (overrides.frequency != null && String(overrides.frequency).trim()) {
      body.frequency = String(overrides.frequency).trim();
    }

    const response = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/medications/pending-intake/${encodedKey}/accept`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      }
    );

    return handleResponse(response);
  },

  dismissPendingIntakeMedication: async (clientReferenceId, intakeEntryKey) => {
    const encodedKey = encodeURIComponent(intakeEntryKey);
    const response = await fetch(
      `${config.api.url}/clients/${clientReferenceId}/medications/pending-intake/${encodedKey}/dismiss`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      }
    );

    return handleResponse(response);
  },

  acceptPendingIntakeMedicationsBatch: async (clientReferenceId, items) => {
    const chunks = chunkPendingIntakeBatch(items, PENDING_INTAKE_BATCH_LIMIT);
    const created = [];
    const notFound = [];

    for (const chunk of chunks) {
      const response = await fetch(
        `${config.api.url}/clients/${clientReferenceId}/medications/pending-intake/accept`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ items: chunk }),
        }
      );
      const result = await handleResponse(response);
      if (Array.isArray(result?.created)) created.push(...result.created);
      if (Array.isArray(result?.notFound)) notFound.push(...result.notFound);
    }

    return { created, notFound };
  },

  dismissPendingIntakeMedicationsBatch: async (clientReferenceId, keys) => {
    const chunks = chunkPendingIntakeBatch(keys, PENDING_INTAKE_BATCH_LIMIT);
    const dismissed = [];
    const notFound = [];

    for (const chunk of chunks) {
      const response = await fetch(
        `${config.api.url}/clients/${clientReferenceId}/medications/pending-intake/dismiss`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ keys: chunk }),
        }
      );
      const result = await handleResponse(response);
      if (Array.isArray(result?.dismissed)) dismissed.push(...result.dismissed);
      if (Array.isArray(result?.notFound)) notFound.push(...result.notFound);
    }

    return { dismissed, notFound };
  },
};
